(function attachTailiVideoSeek(global) {
  'use strict';

  function createCoordinator(environment) {
    const runtime = environment || global;
    const activeRequests = new WeakMap();

    function waitForPresentedFrame(video, request) {
      return new Promise((resolve) => {
        let settled = false;
        let frameCallbackId = null;
        let fallbackTimer = null;

        const finish = (frameWasPresented) => {
          if (settled) return;
          settled = true;
          if (fallbackTimer !== null) runtime.clearTimeout(fallbackTimer);
          resolve(Boolean(frameWasPresented) && activeRequests.get(video) === request);
        };

        request.cancelPresentedFrameWait = () => {
          if (settled) return;
          settled = true;
          if (
            frameCallbackId !== null &&
            typeof video.cancelVideoFrameCallback === 'function'
          ) {
            video.cancelVideoFrameCallback(frameCallbackId);
          }
          if (fallbackTimer !== null) runtime.clearTimeout(fallbackTimer);
          resolve(false);
        };

        if (typeof video.requestVideoFrameCallback === 'function') {
          frameCallbackId = video.requestVideoFrameCallback(() => finish(true));
          fallbackTimer = runtime.setTimeout(() => finish(false), 200);
          return;
        }

        runtime.requestAnimationFrame(() => {
          runtime.requestAnimationFrame(() => finish(true));
        });
      });
    }

    function seek(video, requestedTime) {
      const maximumTime = Math.max(0, (video.duration || 0) - 0.001);
      const targetTime = Math.min(Math.max(requestedTime, 0), maximumTime);
      const previousRequest = activeRequests.get(video);

      if (previousRequest) previousRequest.cancel();

      if (
        !video.seeking &&
        video.readyState >= 2 &&
        Math.abs(video.currentTime - targetTime) <= 0.025
      ) {
        return Promise.resolve(true);
      }

      return new Promise((resolve) => {
        let settled = false;
        let timeoutId = null;

        const clearSeekTimeout = () => {
          if (timeoutId === null) return;
          runtime.clearTimeout(timeoutId);
          timeoutId = null;
        };

        const request = {
          cancelPresentedFrameWait: null,
          cancel() {
            if (settled) return;
            settled = true;
            clearSeekTimeout();
            video.removeEventListener('seeked', handleSeeked);
            video.removeEventListener('error', handleError);
            if (request.cancelPresentedFrameWait) {
              request.cancelPresentedFrameWait();
            }
            if (activeRequests.get(video) === request) {
              activeRequests.delete(video);
            }
            resolve(false);
          }
        };

        const complete = (ready) => {
          if (settled) return;
          settled = true;
          clearSeekTimeout();
          video.removeEventListener('seeked', handleSeeked);
          video.removeEventListener('error', handleError);
          if (activeRequests.get(video) === request) {
            activeRequests.delete(video);
          }
          resolve(ready);
        };

        async function handleSeeked() {
          if (activeRequests.get(video) !== request) {
            request.cancel();
            return;
          }

          if (Math.abs(video.currentTime - targetTime) > 0.05) return;

          const frameReady = await waitForPresentedFrame(video, request);
          if (!frameReady) {
            complete(false);
            return;
          }
          if (
            activeRequests.get(video) === request &&
            !video.seeking &&
            Math.abs(video.currentTime - targetTime) <= 0.05
          ) {
            complete(true);
          }
        }

        function handleError() {
          complete(false);
        }

        activeRequests.set(video, request);
        video.addEventListener('seeked', handleSeeked);
        video.addEventListener('error', handleError, { once: true });
        timeoutId = runtime.setTimeout(() => complete(false), 3000);
        video.currentTime = targetTime;
      });
    }

    return { seek };
  }

  function mapCanonicalToReverse(canonicalTime, duration, reverseDuration) {
    if (!duration || !reverseDuration) return 0;
    const clampedTime = Math.min(Math.max(canonicalTime, 0), duration);
    return Math.min(
      Math.max(reverseDuration - (clampedTime / duration) * reverseDuration, 0),
      reverseDuration
    );
  }

  function createFrameLoop(video, onFrame, environment) {
    const runtime = environment || global;
    let running = false;
    let callbackId = null;
    let callbackMode = null;

    function schedule() {
      if (!running || callbackId !== null) return;

      if (typeof video.requestVideoFrameCallback === 'function') {
        callbackMode = 'video';
        callbackId = video.requestVideoFrameCallback(handleFrame);
        return;
      }

      callbackMode = 'animation';
      callbackId = runtime.requestAnimationFrame((now) => {
        handleFrame(now, { mediaTime: video.currentTime });
      });
    }

    function handleFrame(now, metadata) {
      if (!running) return;
      callbackId = null;
      onFrame(metadata || { mediaTime: video.currentTime }, now);
      schedule();
    }

    function start() {
      if (running) return;
      running = true;
      schedule();
    }

    function stop() {
      if (!running && callbackId === null) return;
      running = false;

      if (callbackId !== null) {
        if (callbackMode === 'video' && typeof video.cancelVideoFrameCallback === 'function') {
          video.cancelVideoFrameCallback(callbackId);
        } else if (callbackMode === 'animation') {
          runtime.cancelAnimationFrame(callbackId);
        }
      }

      callbackId = null;
      callbackMode = null;
    }

    return { start, stop };
  }

  function createProgressWatchdog(onTimeout, timeoutMs, environment) {
    const runtime = environment || global;
    let timerId = null;

    function stop() {
      if (timerId === null) return;
      runtime.clearTimeout(timerId);
      timerId = null;
    }

    function start() {
      stop();
      timerId = runtime.setTimeout(() => {
        timerId = null;
        onTimeout();
      }, timeoutMs);
    }

    return { start, ping: start, stop };
  }

  const api = {
    createCoordinator,
    createFrameLoop,
    createProgressWatchdog,
    mapCanonicalToReverse
  };

  global.TailiVideoSeek = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
